import { createContext, useContext, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ContentDocument, Manifest } from '../content/data';

type LinkContextValue = {
  document: ContentDocument | null;
  manifest: Manifest | null;
};

const LinkContext = createContext<LinkContextValue>({ document: null, manifest: null });

function resolvedInternalHref(document: ContentDocument, manifest: Manifest, href: string): string | null {
  const link = document.links.find((candidate) => candidate.href === href);
  const sourcePath = link?.resolvedSourcePath;
  if (!sourcePath || !link.targetExists) return null;

  if (sourcePath.startsWith('fixtures/')) {
    const filename = sourcePath.split('/').at(-1);
    return filename ? `#/fixture/${encodeURIComponent(filename)}` : null;
  }

  const documentId = manifest.sourcePathToDocumentId[sourcePath];
  if (!documentId) return null;

  if (sourcePath.startsWith('00-orientation/') || !sourcePath.startsWith('week-')) {
    return `#/guide/${encodeURIComponent(documentId)}`;
  }

  const week = manifest.weeks.find((candidate) => sourcePath.startsWith(`${candidate.slug}/`));
  if (!week) return null;

  const dayMatch = sourcePath.match(/\/day-(\d{2})\//);
  if (dayMatch?.[1]) return `#/day/${week.id}-day-${dayMatch[1]}`;
  if (sourcePath.endsWith('/project.md')) return `#/project/${week.id}`;
  return `#/week/${week.id}`;
}

export function documentLinkHref(
  document: ContentDocument | null,
  manifest: Manifest | null,
  href: string | undefined
): string | null {
  if (!href) return null;
  if (/^(https?:|mailto:)/.test(href) || href.startsWith('#')) return href;
  if (!document || !manifest) return null;
  return resolvedInternalHref(document, manifest, href);
}

function MarkdownAnchor({ href, children }: { href?: string | undefined; children?: ReactNode }) {
  const context = useContext(LinkContext);
  const resolved = documentLinkHref(context.document, context.manifest, href);

  if (!resolved) {
    return <span className="unavailable-link">{children}</span>;
  }

  const external = /^https?:/.test(resolved);
  return (
    <a href={resolved} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>
      {children}
      {external ? <span className="sr-only"> (opens in a new tab)</span> : null}
    </a>
  );
}

function MarkdownCode({ className, children }: { className?: string | undefined; children?: ReactNode }) {
  return <code className={className}>{children}</code>;
}

const markdownComponents: Components = {
  a: MarkdownAnchor,
  code: MarkdownCode
};

export function MarkdownView({
  markdown,
  document = null,
  manifest = null,
  className = ''
}: {
  markdown: string;
  document?: ContentDocument | null;
  manifest?: Manifest | null;
  className?: string;
}) {
  return (
    <LinkContext value={{ document, manifest }}>
      <div className={`markdown ${className}`.trim()}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {markdown}
        </ReactMarkdown>
      </div>
    </LinkContext>
  );
}
